'use client';

import { SearchVilla, searchVillas } from '@/actions/villages';
import GlobalLoader from '@/components/GlobalLoader';
import Input from '@/components/Input';
import { useSideMenu } from '@/components/NewHeader/SideMenu/useSideMenu';
import { SearchResultsContainer } from '@/components/NewHeader/SearchResultsContainer';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

export function Search({
  closeDialog,
  inFlow = false,
}: {
  closeDialog?: () => void;
  inFlow?: boolean;
}) {
  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });
  const { newHeader } = useTranslation();
  const { close } = useSideMenu();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [results, setResults] = useState<SearchVilla[]>([]);
  const [lastSearchedQuery, setLastSearchedQuery] = useState<string | null>(
    null,
  );
  const currentRequestId = useRef(0);
  const searchValue = watch('search').trim();

  const handleClose = () => {
    close();
    closeDialog?.();
  };

  useEffect(() => {
    if (searchValue.length < 3) {
      setResults([]);
      setIsLoading(false);
      setHasError(false);
      setLastSearchedQuery(null);
      return;
    }

    setLastSearchedQuery(null);
    setHasError(false);

    const timer = setTimeout(async () => {
      const requestId = ++currentRequestId.current;
      setIsLoading(true);

      try {
        const data = await searchVillas(searchValue);

        if (requestId !== currentRequestId.current) return;

        setResults(data.data);
        setLastSearchedQuery(searchValue);
      } catch {
        if (requestId !== currentRequestId.current) return;
        setResults([]);
        setLastSearchedQuery(searchValue);
        setHasError(true);
      } finally {
        if (requestId === currentRequestId.current) {
          setIsLoading(false);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchValue]);

  return (
    <div className="relative">
      <div className="flex">
        <Input.Container className="grow">
          <Input.Root
            register={register}
            name="search"
            placeholder={newHeader.search.placeholder}
            className="placeholder:text-gray-400 placeholder:text-sm"
          />
        </Input.Container>
      </div>

      <GlobalLoader
        isLoading={isLoading}
        className="!absolute !z-30 !w-full !h-[120px] !top-[44px] !left-0 !bg-white/80"
      />

      <SearchResultsContainer
        isLoading={isLoading}
        hasError={hasError}
        results={results}
        searchValue={searchValue}
        lastSearchedQuery={lastSearchedQuery}
        handleClose={handleClose}
        inFlow={inFlow}
      />
    </div>
  );
}
